// STOREFRONT público del e-commerce (/tienda/:slug/*) — fuera del login,
// multi-tenant por slug (scripts 324/325). Móvil primero. Los precios y la
// disponibilidad SIEMPRE los pone el servidor; el carrito vive en localStorage
// y se respalda como "carrito abandonado" cuando hay contacto capturado.
import { useEffect, useState } from 'react';
import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { uuid } from '@/lib/uuid';
import {
  CheckCircle2, ChevronLeft, Clock, Loader2, Minus, Package, Plus,
  Search, ShoppingCart, Store, Trash2, Truck, Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { LOCALE } from '@/lib/utils/regional';

const usd = (v: number | string | null | undefined) => `$${Number(v ?? 0).toFixed(2)}`;

interface TiendaT {
  valido: boolean; nombre?: string; slug?: string; logo?: string | null;
  whatsapp?: string | null; email?: string | null; color?: string;
  retiro_en_tienda?: boolean; envio_gratis_desde?: number | null;
  colecciones?: { slug: string; nombre: string }[];
  paginas?: { slug: string; titulo: string }[];
  banners?: { titulo: string | null; subtitulo: string | null; imagen: string | null; enlace: string | null }[];
  zonas?: { id: string; nombre: string; tarifa: number }[];
  pagos?: string[];
  destacados?: ItemCatalogo[];
}
interface ItemCatalogo { slug: string; titulo: string; precio: number; disponible: boolean; thumb: string | null }
interface ItemCarrito { producto_id: string; slug: string; titulo: string; precio: number; cantidad: number; thumb: string | null }

const PAGO_LABEL: Record<string, string> = {
  deuna: 'De Una (QR Banco Pichincha)',
  payphone: 'Tarjeta (Payphone)',
  transferencia: 'Transferencia bancaria',
  contraentrega: 'Pago contra entrega',
};

function useCarrito(slug: string) {
  const key = `ecom_cart:${slug}`;
  const [items, setItems] = useState<ItemCarrito[]>(() => {
    try { return JSON.parse(localStorage.getItem(key) ?? '[]'); } catch { return []; }
  });
  useEffect(() => { localStorage.setItem(key, JSON.stringify(items)); }, [key, items]);
  return { items, setItems };
}
function sessionId(slug: string): string {
  const key = `ecom_session:${slug}`;
  let s = localStorage.getItem(key);
  if (!s) { s = uuid(); localStorage.setItem(key, s); }
  return s;
}

export function TiendaPublica() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { data: tienda, isFetching } = useQuery<TiendaT>({
    queryKey: ['ecom-tienda', slug],
    queryFn: async () => (await apiClient.post('/rpc/ecom_tienda', { _slug: slug })).data,
    enabled: !!slug,
    staleTime: 60000,
  });
  const { items, setItems } = useCarrito(slug);
  const nItems = items.reduce((a, i) => a + i.cantidad, 0);

  // Beacon de escaneo del QR phygital (signage 422): si se llegó con ?sp=<pantalla>,
  // cuenta el escaneo (una vez por sesión y pantalla) con el producto aterrizado.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search).get('sp');
    if (!sp || !/^[0-9a-f-]{36}$/i.test(sp)) return;
    const key = `sig_sp:${sp}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    const pslug = window.location.pathname.match(/\/p\/([^/?#]+)/)?.[1] ?? null;
    void apiClient.post('/rpc/sig_qr_escaneo', { _pantalla: sp, _producto: pslug }).catch(() => {});
  }, []);

  // respaldo del carrito (abandonados) — best effort
  useEffect(() => {
    if (!slug || !items.length) return;
    const t = setTimeout(() => {
      void apiClient.post('/rpc/ecom_carrito', {
        _slug: slug, _session: sessionId(slug),
        _data: { items: items.map((i) => ({ producto_id: i.producto_id, titulo: i.titulo, cantidad: i.cantidad, precio: i.precio })) },
      }).catch(() => {});
    }, 2000);
    return () => clearTimeout(t);
  }, [slug, items]);

  if (isFetching && !tienda) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }
  if (tienda && !tienda.valido) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <div><Store className="w-12 h-12 mx-auto text-muted-foreground" />
          <p className="mt-3 text-muted-foreground">Esta tienda no existe o no está activa.</p></div>
      </div>
    );
  }
  if (!tienda) return null;
  const color = tienda.color ?? '#0E7490';

  return (
    <div className="min-h-screen bg-muted/20" style={{ ['--tienda' as string]: color }}>
      <header className="sticky top-0 z-20 bg-card border-b">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to={`/tienda/${slug}`} className="flex items-center gap-2 font-bold text-lg" style={{ color }}>
            {tienda.logo ? <img src={tienda.logo} alt="" className="h-8 max-w-28 object-contain" /> : <Store className="w-6 h-6" />}
            {tienda.nombre}
          </Link>
          <nav className="hidden md:flex gap-3 ml-4 text-sm text-muted-foreground overflow-x-auto">
            {(tienda.colecciones ?? []).slice(0, 5).map((c) => (
              <Link key={c.slug} to={`/tienda/${slug}/c/${c.slug}`} className="hover:text-foreground whitespace-nowrap">{c.nombre}</Link>
            ))}
          </nav>
          <Link to={`/tienda/${slug}/carrito`} className="ml-auto relative p-2 rounded-md border" aria-label="Carrito">
            <ShoppingCart className="w-5 h-5" />
            {nItems > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-white text-xs flex items-center justify-center"
                    style={{ background: color }}>{nItems}</span>
            )}
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-4 pb-24">
        <Routes>
          <Route index element={<VistaHome tienda={tienda} slug={slug} items={items} setItems={setItems} />} />
          <Route path="c/:cslug" element={<VistaCatalogo slug={slug} items={items} setItems={setItems} />} />
          <Route path="buscar" element={<VistaCatalogo slug={slug} items={items} setItems={setItems} />} />
          <Route path="p/:pslug" element={<VistaProducto slug={slug} items={items} setItems={setItems} />} />
          <Route path="carrito" element={<VistaCarrito slug={slug} items={items} setItems={setItems} />} />
          <Route path="checkout" element={<VistaCheckout tienda={tienda} slug={slug} items={items} setItems={setItems} />} />
          <Route path="pedido/:token" element={<VistaPedido tienda={tienda} slug={slug} />} />
          <Route path="pg/:pgslug" element={<VistaPagina slug={slug} />} />
        </Routes>
      </main>

      <footer className="border-t bg-card">
        <div className="max-w-5xl mx-auto px-4 py-6 text-sm text-muted-foreground flex flex-wrap gap-x-6 gap-y-2">
          <span className="font-medium text-foreground">{tienda.nombre}</span>
          {(tienda.paginas ?? []).map((p) => (
            <Link key={p.slug} to={`/tienda/${slug}/pg/${p.slug}`} className="hover:text-foreground">{p.titulo}</Link>
          ))}
          {tienda.whatsapp && (
            <a className="hover:text-foreground" href={`https://wa.me/${tienda.whatsapp.replace(/\D/g, '')}`}
               target="_blank" rel="noopener noreferrer">WhatsApp {tienda.whatsapp}</a>
          )}
          {tienda.email && <span>{tienda.email}</span>}
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
function agregarAlCarrito(setItems: React.Dispatch<React.SetStateAction<ItemCarrito[]>>,
  p: { producto_id?: string; slug: string; titulo: string; precio: number; thumb: string | null }, id?: string) {
  const pid = id ?? p.producto_id ?? '';
  setItems((xs) => {
    const ex = xs.find((x) => x.slug === p.slug);
    if (ex) return xs.map((x) => x === ex ? { ...x, cantidad: x.cantidad + 1 } : x);
    return [...xs, { producto_id: pid, slug: p.slug, titulo: p.titulo, precio: Number(p.precio), cantidad: 1, thumb: p.thumb }];
  });
  toast.success('Agregado al carrito');
}

function TarjetaProducto({ slug, p, onAgregar }: { slug: string; p: ItemCatalogo; onAgregar?: () => void }) {
  return (
    <div className="bg-card border rounded-lg overflow-hidden flex flex-col">
      <Link to={`/tienda/${slug}/p/${p.slug}`} className="block aspect-square bg-muted/40">
        {p.thumb
          ? <img src={p.thumb} alt={p.titulo} loading="lazy" className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Package className="w-10 h-10" /></div>}
      </Link>
      <div className="p-2.5 flex-1 flex flex-col">
        <Link to={`/tienda/${slug}/p/${p.slug}`} className="text-sm font-medium line-clamp-2 flex-1">{p.titulo}</Link>
        <div className="mt-1.5 flex items-center justify-between">
          <span className="font-bold">{usd(p.precio)}</span>
          {p.disponible ? (
            <button className="px-2 py-1 rounded-md text-white text-xs font-medium"
                    style={{ background: 'var(--tienda)' }} onClick={onAgregar}>Agregar</button>
          ) : <span className="text-xs text-muted-foreground">Agotado</span>}
        </div>
      </div>
    </div>
  );
}

function VistaHome({ tienda, slug, setItems }: {
  tienda: TiendaT; slug: string; items: ItemCarrito[];
  setItems: React.Dispatch<React.SetStateAction<ItemCarrito[]>>;
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  return (
    <div className="space-y-6">
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (q.trim()) navigate(`/tienda/${slug}/buscar?q=${encodeURIComponent(q.trim())}`); }}>
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input className="w-full border rounded-md pl-9 pr-3 py-2 text-sm bg-card" placeholder="¿Qué buscas?"
                 value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </form>

      {(tienda.banners ?? []).length > 0 && (
        <div className="space-y-3">
          {(tienda.banners ?? []).slice(0, 3).map((b, i) => {
            const inner = (
              <div className="relative rounded-xl overflow-hidden border bg-card">
                {b.imagen && <img src={b.imagen} alt="" className="w-full max-h-64 object-cover" />}
                {(b.titulo || b.subtitulo) && (
                  <div className={b.imagen ? 'absolute inset-0 bg-black/35 flex flex-col justify-center px-6 text-white' : 'p-6'}>
                    {b.titulo && <div className="text-2xl font-bold">{b.titulo}</div>}
                    {b.subtitulo && <div className="text-sm opacity-90">{b.subtitulo}</div>}
                  </div>
                )}
              </div>
            );
            return b.enlace
              ? <Link key={i} to={b.enlace.startsWith('http') ? b.enlace : `/tienda/${slug}/c/${b.enlace}`}>{inner}</Link>
              : <div key={i}>{inner}</div>;
          })}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {(tienda.colecciones ?? []).map((c) => (
          <Link key={c.slug} to={`/tienda/${slug}/c/${c.slug}`}
                className="px-3 py-1.5 rounded-full border bg-card text-sm hover:border-foreground/40">{c.nombre}</Link>
        ))}
      </div>

      {(tienda.destacados ?? []).length > 0 && (
        <section>
          <h2 className="font-semibold mb-2">Destacados</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {(tienda.destacados ?? []).map((p) => (
              <TarjetaProducto key={p.slug} slug={slug} p={p}
                onAgregar={() => void cargarYAgregar(slug, p.slug, setItems)} />
            ))}
          </div>
        </section>
      )}
      {tienda.envio_gratis_desde != null && (
        <p className="text-center text-sm text-muted-foreground">
          🚚 Envío GRATIS en compras desde {usd(tienda.envio_gratis_desde)}
        </p>
      )}
    </div>
  );
}

// La tarjeta no conoce el producto_id: lo resuelve la ficha antes de agregar
async function cargarYAgregar(slug: string, pslug: string, setItems: React.Dispatch<React.SetStateAction<ItemCarrito[]>>) {
  try {
    const d = (await apiClient.post('/rpc/ecom_producto', { _slug: slug, _pslug: pslug })).data;
    if (!d?.valido) { toast.error('Producto no disponible'); return; }
    agregarAlCarrito(setItems, { producto_id: d.producto_id, slug: pslug, titulo: d.titulo, precio: Number(d.precio), thumb: d.thumb });
  } catch (e) { toast.error(getErrorMessage(e)); }
}

function VistaCatalogo({ slug, setItems }: {
  slug: string; items: ItemCarrito[]; setItems: React.Dispatch<React.SetStateAction<ItemCarrito[]>>;
}) {
  const { cslug } = useParams<{ cslug?: string }>();
  const q = new URLSearchParams(window.location.search).get('q') ?? '';
  const [pagina, setPagina] = useState(0);
  const { data } = useQuery<{ valido: boolean; total: number; items: ItemCatalogo[] }>({
    queryKey: ['ecom-catalogo', slug, cslug, q, pagina],
    queryFn: async () => (await apiClient.post('/rpc/ecom_catalogo', {
      _slug: slug, _coleccion: cslug ?? null, _q: q || null, _limit: 24, _offset: pagina * 24 })).data,
  });
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Link to={`/tienda/${slug}`} className="p-1.5 rounded-md border bg-card"><ChevronLeft className="w-4 h-4" /></Link>
        <h1 className="font-semibold capitalize">{cslug?.replace(/-/g, ' ') ?? (q ? `Resultados: "${q}"` : 'Catálogo')}</h1>
        <span className="text-sm text-muted-foreground">({data?.total ?? '…'})</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {(data?.items ?? []).map((p) => (
          <TarjetaProducto key={p.slug} slug={slug} p={p}
            onAgregar={() => void cargarYAgregar(slug, p.slug, setItems)} />
        ))}
      </div>
      {!data?.items?.length && <p className="py-10 text-center text-muted-foreground">No hay productos aquí todavía.</p>}
      {(data?.total ?? 0) > 24 && (
        <div className="flex justify-center gap-2">
          <button className="px-3 py-1 border rounded-md bg-card disabled:opacity-40" disabled={pagina === 0}
                  onClick={() => setPagina((p) => p - 1)}>← Anterior</button>
          <button className="px-3 py-1 border rounded-md bg-card disabled:opacity-40"
                  disabled={(pagina + 1) * 24 >= (data?.total ?? 0)}
                  onClick={() => setPagina((p) => p + 1)}>Siguiente →</button>
        </div>
      )}
    </div>
  );
}

function VistaProducto({ slug, setItems }: {
  slug: string; items: ItemCarrito[]; setItems: React.Dispatch<React.SetStateAction<ItemCarrito[]>>;
}) {
  const { pslug = '' } = useParams<{ pslug: string }>();
  const navigate = useNavigate();
  const { data: p } = useQuery<{ valido: boolean; producto_id: string; titulo: string; descripcion: string | null;
    precio: number; disponible: number; imagen: string | null; thumb: string | null }>({
    queryKey: ['ecom-producto', slug, pslug],
    queryFn: async () => (await apiClient.post('/rpc/ecom_producto', { _slug: slug, _pslug: pslug })).data,
  });
  useEffect(() => { if (p?.titulo) document.title = `${p.titulo}`; }, [p?.titulo]);
  if (!p) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (!p.valido) return <p className="py-16 text-center text-muted-foreground">Este producto ya no está disponible.</p>;
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-card border rounded-xl overflow-hidden aspect-square">
        {p.imagen || p.thumb
          ? <img src={p.imagen ?? p.thumb ?? ''} alt={p.titulo} className="w-full h-full object-contain" />
          : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Package className="w-16 h-16" /></div>}
      </div>
      <div className="space-y-3">
        <button className="text-sm text-muted-foreground hover:text-foreground" onClick={() => navigate(-1)}>← Volver</button>
        <h1 className="text-2xl font-bold">{p.titulo}</h1>
        <div className="text-3xl font-bold" style={{ color: 'var(--tienda)' }}>{usd(p.precio)}</div>
        {p.disponible > 0
          ? <p className="text-sm text-green-600">✔ Disponible{p.disponible <= 5 ? ` (¡quedan ${p.disponible}!)` : ''}</p>
          : <p className="text-sm text-red-500">Agotado por el momento</p>}
        <button className="w-full md:w-auto px-8 py-3 rounded-lg text-white font-semibold disabled:opacity-40"
                style={{ background: 'var(--tienda)' }} disabled={p.disponible <= 0}
                onClick={() => {
                  agregarAlCarrito(setItems, { producto_id: p.producto_id, slug: pslug, titulo: p.titulo, precio: Number(p.precio), thumb: p.thumb });
                  navigate(`/tienda/${slug}/carrito`);
                }}>
          <ShoppingCart className="w-4 h-4 inline mr-2" /> Agregar al carrito
        </button>
        {p.descripcion && <div className="text-sm text-muted-foreground whitespace-pre-line border-t pt-3">{p.descripcion}</div>}
      </div>
    </div>
  );
}

function VistaCarrito({ slug, items, setItems }: {
  slug: string; items: ItemCarrito[]; setItems: React.Dispatch<React.SetStateAction<ItemCarrito[]>>;
}) {
  const navigate = useNavigate();
  const total = items.reduce((a, i) => a + i.precio * i.cantidad, 0);
  return (
    <div className="max-w-xl mx-auto space-y-3">
      <h1 className="font-semibold text-lg">Tu carrito</h1>
      {!items.length && (
        <div className="py-14 text-center text-muted-foreground">
          <ShoppingCart className="w-10 h-10 mx-auto mb-2" />
          Está vacío. <Link to={`/tienda/${slug}`} className="underline">Ir a la tienda</Link>
        </div>
      )}
      {items.map((i) => (
        <div key={i.slug} className="bg-card border rounded-lg p-3 flex items-center gap-3">
          {i.thumb ? <img src={i.thumb} alt="" className="w-14 h-14 rounded object-cover" />
            : <div className="w-14 h-14 rounded bg-muted flex items-center justify-center"><Package className="w-6 h-6 text-muted-foreground" /></div>}
          <div className="flex-1">
            <div className="text-sm font-medium">{i.titulo}</div>
            <div className="text-sm text-muted-foreground">{usd(i.precio)} c/u</div>
          </div>
          <div className="flex items-center gap-2">
            <button className="w-7 h-7 rounded-full border flex items-center justify-center"
                    onClick={() => setItems((xs) => i.cantidad <= 1 ? xs.filter((x) => x !== i)
                      : xs.map((x) => x === i ? { ...x, cantidad: x.cantidad - 1 } : x))}><Minus className="w-3.5 h-3.5" /></button>
            <span className="w-5 text-center font-medium">{i.cantidad}</span>
            <button className="w-7 h-7 rounded-full border flex items-center justify-center"
                    onClick={() => setItems((xs) => xs.map((x) => x === i ? { ...x, cantidad: x.cantidad + 1 } : x))}><Plus className="w-3.5 h-3.5" /></button>
          </div>
          <button onClick={() => setItems((xs) => xs.filter((x) => x !== i))}><Trash2 className="w-4 h-4 text-red-500" /></button>
        </div>
      ))}
      {items.length > 0 && (
        <>
          <div className="flex justify-between font-semibold text-lg border-t pt-3">
            <span>Subtotal</span><span>{usd(total)}</span>
          </div>
          <p className="text-xs text-muted-foreground">El IVA y el envío se calculan en el siguiente paso.</p>
          <button className="w-full py-3 rounded-lg text-white font-semibold" style={{ background: 'var(--tienda)' }}
                  onClick={() => navigate(`/tienda/${slug}/checkout`)}>
            Finalizar compra
          </button>
        </>
      )}
    </div>
  );
}

function VistaCheckout({ tienda, slug, items, setItems }: {
  tienda: TiendaT; slug: string; items: ItemCarrito[];
  setItems: React.Dispatch<React.SetStateAction<ItemCarrito[]>>;
}) {
  const navigate = useNavigate();
  const [f, setF] = useState({ nombre: '', email: '', telefono: '', identificacion: '', tipo_identificacion: 'CEDULA',
    entrega_metodo: (tienda.retiro_en_tienda ? 'retiro' : 'envio'), zona_id: '', direccion: '', ciudad: '', referencia: '',
    pago_metodo: (tienda.pagos ?? [])[0] ?? '', notas: '' });
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const sub = items.reduce((a, i) => a + i.precio * i.cantidad, 0);
  const zona = (tienda.zonas ?? []).find((z) => z.id === f.zona_id);
  const envio = f.entrega_metodo === 'retiro' ? 0
    : (tienda.envio_gratis_desde != null && sub >= Number(tienda.envio_gratis_desde)) ? 0
    : Number(zona?.tarifa ?? 0);

  // captura de contacto para el carrito abandonado
  useEffect(() => {
    if (!f.email && !f.telefono) return;
    const t = setTimeout(() => {
      void apiClient.post('/rpc/ecom_carrito', { _slug: slug, _session: sessionId(slug),
        _data: { nombre: f.nombre, email: f.email, telefono: f.telefono } }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [slug, f.nombre, f.email, f.telefono]);

  const crear = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/ecom_pedido_crear', {
      _slug: slug,
      _payload: {
        ...f, session_uuid: sessionId(slug),
        items: items.map((i) => ({ producto_id: i.producto_id, cantidad: i.cantidad })),
      },
    })).data,
    onSuccess: (r: { token: string }) => {
      setItems([]);
      navigate(`/tienda/${slug}/pedido/${r.token}`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!items.length) {
    return <p className="py-14 text-center text-muted-foreground">El carrito está vacío. <Link className="underline" to={`/tienda/${slug}`}>Volver</Link></p>;
  }
  const inp = 'w-full border rounded-md px-3 py-2 text-sm bg-card';
  return (
    <div className="max-w-xl mx-auto space-y-4">
      <h1 className="font-semibold text-lg">Finalizar compra</h1>

      <section className="bg-card border rounded-lg p-3 space-y-2">
        <h2 className="text-sm font-semibold">1 · Tus datos</h2>
        <input className={inp} placeholder="Nombre completo *" value={f.nombre} onChange={(e) => set('nombre', e.target.value)} />
        <div className="grid grid-cols-2 gap-2">
          <input className={inp} type="email" placeholder="Email" value={f.email} onChange={(e) => set('email', e.target.value)} />
          <input className={inp} placeholder="Teléfono / WhatsApp" value={f.telefono} onChange={(e) => set('telefono', e.target.value)} />
        </div>
        <div className="grid grid-cols-[110px_1fr] gap-2">
          <select className={inp} value={f.tipo_identificacion} onChange={(e) => set('tipo_identificacion', e.target.value)}>
            <option value="CEDULA">Cédula</option><option value="RUC">RUC</option><option value="PASAPORTE">Pasaporte</option>
          </select>
          <input className={inp} placeholder="Identificación (para tu factura; vacío = consumidor final)"
                 value={f.identificacion} onChange={(e) => set('identificacion', e.target.value)} />
        </div>
      </section>

      <section className="bg-card border rounded-lg p-3 space-y-2">
        <h2 className="text-sm font-semibold">2 · Entrega</h2>
        <div className="flex gap-2">
          {tienda.retiro_en_tienda && (
            <button className={`flex-1 px-3 py-2 rounded-md border text-sm ${f.entrega_metodo === 'retiro' ? 'border-2 font-semibold' : ''}`}
                    style={f.entrega_metodo === 'retiro' ? { borderColor: 'var(--tienda)' } : {}}
                    onClick={() => set('entrega_metodo', 'retiro')}>
              <Store className="w-4 h-4 inline mr-1" /> Retiro en tienda (gratis)
            </button>
          )}
          <button className={`flex-1 px-3 py-2 rounded-md border text-sm ${f.entrega_metodo === 'envio' ? 'border-2 font-semibold' : ''}`}
                  style={f.entrega_metodo === 'envio' ? { borderColor: 'var(--tienda)' } : {}}
                  onClick={() => set('entrega_metodo', 'envio')}>
            <Truck className="w-4 h-4 inline mr-1" /> Envío a domicilio
          </button>
        </div>
        {f.entrega_metodo === 'envio' && (
          <>
            <select className={inp} value={f.zona_id} onChange={(e) => set('zona_id', e.target.value)}>
              <option value="">— Zona de envío —</option>
              {(tienda.zonas ?? []).map((z) => (
                <option key={z.id} value={z.id}>{z.nombre} ({Number(z.tarifa) === 0 ? 'gratis' : usd(z.tarifa)})</option>
              ))}
            </select>
            <input className={inp} placeholder="Dirección de entrega *" value={f.direccion} onChange={(e) => set('direccion', e.target.value)} />
            <div className="grid grid-cols-2 gap-2">
              <input className={inp} placeholder="Ciudad" value={f.ciudad} onChange={(e) => set('ciudad', e.target.value)} />
              <input className={inp} placeholder="Referencia" value={f.referencia} onChange={(e) => set('referencia', e.target.value)} />
            </div>
          </>
        )}
      </section>

      <section className="bg-card border rounded-lg p-3 space-y-2">
        <h2 className="text-sm font-semibold">3 · Pago</h2>
        {(tienda.pagos ?? []).map((m) => (
          <label key={m} className="flex items-center gap-2 text-sm cursor-pointer border rounded-md px-3 py-2"
                 style={f.pago_metodo === m ? { borderColor: 'var(--tienda)', borderWidth: 2 } : {}}>
            <input type="radio" checked={f.pago_metodo === m} onChange={() => set('pago_metodo', m)} />
            {PAGO_LABEL[m] ?? m}
          </label>
        ))}
        <input className={inp} placeholder="Notas del pedido (opcional)" value={f.notas} onChange={(e) => set('notas', e.target.value)} />
      </section>

      <section className="bg-card border rounded-lg p-3 text-sm space-y-1">
        <div className="flex justify-between"><span>Subtotal</span><span>{usd(sub)}</span></div>
        <div className="flex justify-between"><span>Envío</span>
          <span>{f.entrega_metodo === 'retiro' ? 'Gratis (retiro)' : zona ? (envio === 0 ? '¡GRATIS!' : usd(envio)) : 'según zona'}</span></div>
        <p className="text-xs text-muted-foreground">+ IVA según producto (verás el total exacto al confirmar).</p>
      </section>

      <button className="w-full py-3 rounded-lg text-white font-semibold disabled:opacity-40" style={{ background: 'var(--tienda)' }}
              disabled={crear.isPending || !f.nombre.trim() || (!f.email.trim() && !f.telefono.trim())
                || !f.pago_metodo || (f.entrega_metodo === 'envio' && (!f.zona_id || !f.direccion.trim()))}
              onClick={() => crear.mutate()}>
        {crear.isPending ? <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> : null}
        Confirmar pedido
      </button>
    </div>
  );
}

function VistaPedido({ tienda, slug }: { tienda: TiendaT; slug: string }) {
  const { token = '' } = useParams<{ token: string }>();
  const qc = useQueryClient();
  const [subiendo, setSubiendo] = useState(false);
  const { data: p } = useQuery<{ valido: boolean; codigo: string; estado: string; total: number; subtotal: number;
    iva: number; envio: number; pago_metodo: string; entrega_metodo: string; zona: string | null; guia: string | null;
    transferencia?: string | null; pago_por_confirmar?: boolean;
    lineas: { titulo: string; cantidad: number; subtotal: number }[];
    eventos: { evento: string; fecha: string }[] }>({
    queryKey: ['ecom-pedido', token],
    queryFn: async () => (await apiClient.post('/rpc/ecom_pedido', { _token: token })).data,
    enabled: !!token,
    staleTime: 0,
    // El pago lo confirma la pasarela (webhook) o el comercio: no hay nada que
    // se resuelva en segundos, así que no tiene sentido consultar cada 3,5 s.
    refetchInterval: (q) => q.state.data?.estado === 'pendiente_pago' ? 15000 : 30000,
  });
  if (!p) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (!p.valido) return <p className="py-16 text-center text-muted-foreground">Pedido no encontrado.</p>;

  const PASOS = ['pendiente_pago', 'pagado', 'en_preparacion', 'enviado', 'entregado'];
  const idx = PASOS.indexOf(p.estado);
  const subirComprobante = (file: File | null) => {
    if (!file) return;
    setSubiendo(true);
    const r = new FileReader();
    r.onload = async () => {
      try {
        await apiClient.post('/rpc/ecom_comprobante', { _token: token, _imagen: String(r.result) });
        toast.success('Comprobante recibido: lo validaremos en breve');
        void qc.invalidateQueries({ queryKey: ['ecom-pedido'] });
      } catch (e) { toast.error(getErrorMessage(e)); }
      finally { setSubiendo(false); }
    };
    r.readAsDataURL(file);
  };

  return (
    <div className="max-w-xl mx-auto space-y-4">
      <div className="text-center">
        {p.estado === 'pendiente_pago' && p.pago_metodo !== 'transferencia'
          ? <Clock className="w-12 h-12 mx-auto animate-pulse" style={{ color: 'var(--tienda)' }} />
          : <CheckCircle2 className="w-12 h-12 mx-auto text-green-600" />}
        <h1 className="text-xl font-bold mt-2">Pedido {p.codigo}</h1>
        <p className="text-sm text-muted-foreground">
          {p.estado === 'pendiente_pago' && (p.pago_metodo === 'deuna' || p.pago_metodo === 'payphone')
            ? 'Estamos validando tu pago. Te avisamos por correo en cuanto se confirme — puedes cerrar esta página.'
            : p.estado === 'pendiente_pago' ? 'Esperando tu transferencia.'
            : p.estado === 'cancelado' ? 'Pedido cancelado.'
            : '¡Gracias por tu compra! Guarda este enlace para seguir tu pedido.'}
        </p>
      </div>

      {p.estado !== 'cancelado' && p.estado !== 'reembolsado' && (
        <div className="flex items-center">
          {PASOS.map((paso, i) => (
            <div key={paso} className="flex-1 flex items-center">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0`}
                   style={{ background: i <= idx ? 'var(--tienda)' : '#9ca3af55' }}>{i + 1}</div>
              {i < PASOS.length - 1 && <div className="flex-1 h-1 mx-1 rounded"
                   style={{ background: i < idx ? 'var(--tienda)' : '#9ca3af33' }} />}
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-5 text-[10px] text-center text-muted-foreground -mt-2">
        <span>Pago</span><span>Pagado</span><span>Preparando</span><span>Enviado</span><span>Entregado</span>
      </div>

      {p.estado === 'pendiente_pago' && p.pago_metodo === 'transferencia' && (
        <div className="bg-card border rounded-lg p-3 space-y-2 text-sm">
          <div className="font-semibold">Transfiere y sube tu comprobante</div>
          <p className="whitespace-pre-line text-muted-foreground">{p.transferencia ?? 'Solicita los datos bancarios a la tienda.'}</p>
          <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg py-4 cursor-pointer text-muted-foreground">
            {subiendo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Subir comprobante (foto)
            <input type="file" accept="image/*" className="hidden" onChange={(e) => subirComprobante(e.target.files?.[0] ?? null)} />
          </label>
        </div>
      )}

      <div className="bg-card border rounded-lg p-3 text-sm">
        {p.lineas.map((l, i) => (
          <div key={i} className="flex justify-between py-0.5">
            <span>{Number(l.cantidad)}× {l.titulo}</span><span className="font-mono">{usd(l.subtotal)}</span>
          </div>
        ))}
        <div className="border-t mt-2 pt-2 space-y-0.5">
          <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{usd(p.subtotal)}</span></div>
          <div className="flex justify-between"><span>IVA</span><span className="font-mono">{usd(p.iva)}</span></div>
          <div className="flex justify-between"><span>Envío</span><span className="font-mono">{Number(p.envio) === 0 ? 'Gratis' : usd(p.envio)}</span></div>
          <div className="flex justify-between font-bold text-base"><span>Total</span><span className="font-mono">{usd(p.total)}</span></div>
        </div>
      </div>

      {p.guia && <p className="text-sm text-center">📦 Guía de envío: <span className="font-mono font-semibold">{p.guia}</span></p>}

      <div className="bg-card border rounded-lg p-3 text-xs space-y-1">
        {p.eventos.map((e, i) => (
          <div key={i} className="flex justify-between gap-3">
            <span>{e.evento}</span>
            <span className="text-muted-foreground whitespace-nowrap">{new Date(e.fecha).toLocaleString(LOCALE, { hour12: false })}</span>
          </div>
        ))}
      </div>

      <p className="text-center text-sm">
        <Link to={`/tienda/${slug}`} className="underline">Seguir comprando en {tienda.nombre}</Link>
      </p>
    </div>
  );
}

function VistaPagina({ slug }: { slug: string }) {
  const { pgslug = '' } = useParams<{ pgslug: string }>();
  const { data } = useQuery<{ valido: boolean; titulo?: string; contenido?: string }>({
    queryKey: ['ecom-pagina', slug, pgslug],
    queryFn: async () => (await apiClient.post('/rpc/ecom_pagina', { _slug: slug, _pgslug: pgslug })).data,
  });
  if (!data) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (!data.valido) return <p className="py-16 text-center text-muted-foreground">Página no encontrada.</p>;
  return (
    <article className="max-w-2xl mx-auto bg-card border rounded-xl p-6">
      <h1 className="text-xl font-bold mb-3">{data.titulo}</h1>
      <div className="text-sm text-muted-foreground whitespace-pre-line">{data.contenido}</div>
    </article>
  );
}
